import { useCallback, useMemo, useState, type RefObject } from "react";
import { Icons, Spinner } from "@/components/ui";
import { ContextMenuItem } from "@/components/ui/context-menu";
import { useAppLanguage, useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import type { FrameData } from "@/lib/frame-data";
import { copyFrame, downloadFrame } from "@/lib/frame-menu-actions";
import { frameHasCopyText } from "./frame-copy";
import { frameDownloadOptions } from "./frame-download";
import { useFrameDetail } from "./use-frame-detail";

interface FrameContentMenuItemsProps {
  frame: FrameData;
  menuRootRef: RefObject<HTMLDivElement | null>;
  selectedTextRef: RefObject<string>;
}

/**
 * The context menu's Copy and Download items, which act on the frame's full
 * content: mounted with the open menu, they load a card-only frame's detail
 * and stay disabled with a spinner until it arrives. Copy shows only when it
 * has text to write: the selection the menu opened on, or the content.
 */
export function FrameContentMenuItems({
  frame,
  menuRootRef,
  selectedTextRef,
}: FrameContentMenuItemsProps) {
  const tCommon = useT(commonMessages);
  const language = useAppLanguage();
  const cardOnly = frame.contentState === "card";
  const { loading, failed } = useFrameDetail(frame.frameId, cardOnly);
  // Read once as the menu opens: the frame's root and the selection the
  // menu opened on. A card-only frame's Copy waits for its detail; a failed
  // detail has nothing to copy (its error toast has shown).
  const [opened] = useState(() => ({
    root: menuRootRef.current,
    selection: selectedTextRef.current,
  }));
  const copyable = useMemo(
    () => frameHasCopyText(frame, opened.root, opened.selection),
    [frame, opened],
  );
  const showCopy = cardOnly ? !failed : copyable;
  const handleCopy = useCallback(() => {
    void copyFrame(frame, {
      root: menuRootRef.current,
      selection: selectedTextRef.current,
    });
  }, [frame, menuRootRef, selectedTextRef]);
  // The option labels are translated text, so they follow the language too.
  const downloadOptions = useMemo(
    () => (language && !cardOnly ? frameDownloadOptions(frame) : []),
    [cardOnly, frame, language],
  );
  return (
    <>
      {showCopy && (
        <ContextMenuItem
          icon={
            loading ? (
              <Spinner data-testid="ctx-copy-loading" />
            ) : (
              <Icons.Copy />
            )
          }
          onClick={handleCopy}
          disabled={cardOnly}
          data-testid="ctx-copy"
        >
          {tCommon("copy")}
        </ContextMenuItem>
      )}
      {downloadOptions.map((option) => (
        <ContextMenuItem
          key={option.fileName}
          icon={<Icons.Download />}
          onClick={() =>
            void downloadFrame(frame, { option, root: menuRootRef.current })
          }
          data-testid={`ctx-download-${option.extension}-${option.fileName}`}
        >
          {option.label}
        </ContextMenuItem>
      ))}
    </>
  );
}
